'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { BarChart2, FileText } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  featured_image?: string;
  published_at?: string;
  category?: string;
  author_name?: string;
}

export default function BlogIndexPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [categories, setCategories] = useState<string[]>(['All']);

  useEffect(() => {
    const fetchPosts = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, featured_image, published_at, category, author_name')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (data) {
        setPosts(data);
        const cats = Array.from(new Set(data.map((p) => p.category).filter(Boolean))) as string[];
        setCategories(['All', ...cats]);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const filteredPosts = activeCategory === 'All' ? posts : posts.filter((p) => p.category === activeCategory);
  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div style={{ background: '#060B14', minHeight: '100vh' }}>
      {/* Hero */}
      <section
        style={{
          background: '#060B14',
          padding: '88px 40px 72px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse 65% 45% at 50% 0%, rgba(249,115,22,0.09), transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
            maskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#F97316',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '18px',
            }}
          >
            Blog
          </span>
          <h1
            style={{
              fontSize: 'clamp(36px, 5vw, 58px)',
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              color: 'white',
              marginBottom: '16px',
            }}
          >
            Marketing Intelligence
          </h1>
          <p style={{ fontSize: '17px', color: '#94A3B8', lineHeight: 1.65 }}>
            Insights, strategies, and data-driven advice for performance marketers.
          </p>
        </div>
      </section>

      <section style={{ padding: '72px 40px 88px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Category Tabs */}
          {categories.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '40px' }}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    padding: '7px 16px',
                    borderRadius: '100px',
                    border: activeCategory === cat ? 'none' : '1px solid rgba(255,255,255,0.08)',
                    background: activeCategory === cat ? 'linear-gradient(135deg, #F97316, #EA580C)' : 'rgba(255,255,255,0.04)',
                    color: activeCategory === cat ? 'white' : '#94A3B8',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '2px solid #F97316',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontSize: '16px', color: '#64748B' }}>No posts published yet. Check back soon!</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && (
                <div style={{ marginBottom: '48px' }}>
                  <Link href={`/blog/${featuredPost.slug}`} style={{ textDecoration: 'none', display: 'block' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.2)';
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                      }}
                    >
                      {featuredPost.featured_image ? (
                        <img src={featuredPost.featured_image} alt={featuredPost.title} style={{ width: '100%', height: '280px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '280px', background: 'rgba(249,115,22,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BarChart2 size={56} style={{ color: '#F97316', opacity: 0.4 }} />
                        </div>
                      )}
                      <div style={{ padding: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {featuredPost.category && (
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>{featuredPost.category}</span>
                        )}
                        <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 26px)', fontWeight: 700, color: 'white', marginBottom: '12px', lineHeight: 1.3 }}>{featuredPost.title}</h2>
                        {featuredPost.excerpt && (
                          <p style={{ fontSize: '14px', color: '#94A3B8', lineHeight: 1.65, marginBottom: '16px' }}>{featuredPost.excerpt}</p>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748B' }}>
                          {featuredPost.author_name && <span>{featuredPost.author_name}</span>}
                          {featuredPost.author_name && featuredPost.published_at && <span>·</span>}
                          {featuredPost.published_at && <span>{formatDate(featuredPost.published_at)}</span>}
                        </div>
                        <span style={{ marginTop: '16px', fontSize: '13px', fontWeight: 700, color: '#F97316' }}>Read More →</span>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Remaining Posts Grid */}
              {remainingPosts.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                  {remainingPosts.map((post) => (
                    <Link
                      key={post.id}
                      href={`/blog/${post.slug}`}
                      style={{ textDecoration: 'none', display: 'block' }}
                    >
                      <div
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          height: '100%',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.2)';
                          (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                        }}
                      >
                        {post.featured_image ? (
                          <img src={post.featured_image} alt={post.title} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '180px', background: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FileText size={40} style={{ color: '#475569' }} />
                          </div>
                        )}
                        <div style={{ padding: '24px' }}>
                          {post.category && (
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{post.category}</span>
                          )}
                          <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginTop: '8px', marginBottom: '8px', lineHeight: 1.4 }}>{post.title}</h3>
                          {post.excerpt && <p style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.6, marginBottom: '12px' }}>{post.excerpt}</p>}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '12px', color: '#64748B' }}>{formatDate(post.published_at)}</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#F97316' }}>Read More →</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
